import { SessionService } from '../session/session.service';
import { Injectable, Optional, SkipSelf } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { MatSnackBar } from '@angular/material/snack-bar'
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { timeout } from 'rxjs';
import { AppConfigService } from '../app-config/app-config.service';
import { AutorizacaoTokenStore } from '../permissao/autorizacao-token.store';
import { mensagemDoCodigoDeAutorizacao, RequerPermissaoHeader } from 'src/app/models/User/MinhaAcaoPermissao.model';

export interface ErrorResponse {
    property: string;
    message: string;
}

export interface ApiSuccessResponse<T> {
    success: true;
    message?: string;
    messageCode?: number;
    data: T;
    statusCode?: number;
}

export interface ApiErrorResponse {
    success: false;
    messageCode: number;
    message?: string;
    data: ErrorResponse[];
    statusCode?: number;
}

type ApiResponse<T> = ApiSuccessResponse<T> | ApiErrorResponse;

// Validação do modal de senha de autorizado: a falha de conexão ou de servidor aparece no próprio modal
const ROTAS_COM_ERRO_TRATADO_PELO_CHAMADOR = ['/v1/autorizacao/validar'];
// Sem resposta nesse tempo, a validação falha e o modal volta a permitir nova tentativa
const TEMPO_LIMITE_DA_VALIDACAO_MS = 15000;

@Injectable({
    providedIn: 'root'
})
export class ApiService {

    constructor(
        @Optional() @SkipSelf() parentModule: ApiService,
        private http: HttpClient,
        private session: SessionService,
        private snackBar: MatSnackBar,
        private translateService: TranslateService,
        private router: Router // Adicionado para redirecionamento
    ,
        private appConfig: AppConfigService,
        private autorizacaoTokens: AutorizacaoTokenStore) {
        if (parentModule) {
            throw new Error('ApiService is already loaded');
        }
    }

    get<T>(url: string, queryParams?: any, tokenOverride?: string): Promise<ApiResponse<T>> {
        return this.request('GET', url, null, queryParams, tokenOverride);
    }

    post<T>(url: string, data: any, tokenOverride?: string): Promise<ApiResponse<T>> {
        return this.request('POST', url, data, undefined, tokenOverride);
    }

    put<T>(url: string, data: any, tokenOverride?: string): Promise<ApiResponse<T>> {
        return this.request('PUT', url, data, undefined, tokenOverride);
    }

    patch<T>(url: string, data: any, tokenOverride?: string): Promise<ApiResponse<T>> {
        return this.request('PATCH', url, data, undefined, tokenOverride);
    }

    delete<T>(url: string, tokenOverride?: string): Promise<ApiResponse<T>> {
        return this.request('DELETE', url, null, undefined, tokenOverride);
    }

    async isServerAvailable(): Promise<boolean> {
        try {
            await this.http.get(environment.apiUrl + '/status', { observe: 'response', responseType: 'text' }).toPromise();
            return true;
        } catch (error: any) {
            return !this.isServerUnavailable(error.status);
        }
    }

    private isServerUnavailable(status: number): boolean {
        return status === 0 || (status >= 500 && status < 600);
    }

    private redirectToServerError(): void {
        if (this.router.url.startsWith('/server-error')) {
            return;
        }
        sessionStorage.setItem('serverErrorReturnUrl', this.router.url);
        this.router.navigate(['/server-error']);
    }

    private getPartner(): string {
        return this.appConfig.partnerKey;
    }

    // Renovação em andamento compartilhada: várias requisições com 401 ao mesmo tempo geram um único refresh
    private renovacaoEmAndamento?: Promise<boolean>;

    private renovarSessao(): Promise<boolean> {
        if (!this.renovacaoEmAndamento) {
            this.renovacaoEmAndamento = this.executarRenovacao().finally(() => this.renovacaoEmAndamento = undefined);
        }
        return this.renovacaoEmAndamento;
    }

    private async executarRenovacao(): Promise<boolean> {
        const token = this.session.token;
        const refreshToken = this.session.refreshToken;
        if (!token || !refreshToken) {
            return false;
        }
        try {
            // Sem Authorization: o endpoint recebe o token vencido no corpo
            const res: any = await this.http.post(environment.apiUrl + '/authentication/refresh-authorization',
                { token, refreshToken },
                { headers: { 'X-Partner': this.getPartner() } }).toPromise();
            if (res?.success && res.data?.token && res.data?.refreshToken) {
                this.session.setToken({ token: res.data.token, refreshToken: res.data.refreshToken });
                return true;
            }
        } catch { }
        return false;
    }

    private encerrarSessaoExpirada(): void {
        this.session.logout();
        window.location.href = '/sign-in';
    }

    private async request<T>(method: string, url: string, data?: any, queryParams?: any, tokenOverride?: string, jaRenovou: boolean = false): Promise<ApiResponse<T>> {
        if (url[0] !== '/') {
            throw new Error('Missing leading slash in ' + url);
        }

        let urlSearchParams: URLSearchParams | undefined = undefined;;
        if (queryParams) {
            urlSearchParams = new URLSearchParams();
            const keys: string[] = Object.keys(queryParams);
            const values: string[] = Object.values(queryParams);

            for (let index = 0; index < keys.length; index++) {
                if (Array.isArray(values[index])) {
                    queryParams[keys[index]].forEach((value: any) => {
                        urlSearchParams?.append(keys[index], value);
                    });
                }
                else {
                    if (typeof(values[index]) == 'undefined') {
                        values[index] = '';
                    }
                    urlSearchParams.append(keys[index], values[index]);
                }
            }
        }

        let fullUrl = environment.apiUrl + url;

        if (urlSearchParams)
            fullUrl = fullUrl + '?' + urlSearchParams.toString();

        const options: any = {
            body: data,
            headers: {},
        };

        options.headers['X-Partner'] = this.getPartner();

        const authToken = tokenOverride || this.session.token;
        if (authToken) {
            options.headers['Authorization'] = "Bearer " + authToken;
        }
        // Token do modal de senha (ações 2 e 4) nas rotas que o exigem
        const tokenAutorizacao = this.autorizacaoTokens.obterParaRequisicao(method, url);
        if (tokenAutorizacao) {
            options.headers[RequerPermissaoHeader] = tokenAutorizacao;
        }

        if (this.session.isLogged && this.session.getDetails().language)
            options.headers['Accept-Language'] = this.session.getDetails().language;

        try {
            const requisicao$ = this.http.request<ApiResponse<T>>(method, fullUrl, {
                observe: 'response',
                ...options,
            });
            const res: any = await (ROTAS_COM_ERRO_TRATADO_PELO_CHAMADOR.includes(url)
                ? requisicao$.pipe(timeout(TEMPO_LIMITE_DA_VALIDACAO_MS))
                : requisicao$).toPromise();
            return res.body;
        } catch (error: any) {
            // Token vencido: renova com o refresh token e repete a requisição uma vez
            if (error?.status === 401 && !tokenOverride && !jaRenovou && this.session.isLogged
                && !url.startsWith('/authentication/')) {
                if (await this.renovarSessao()) {
                    return this.request<T>(method, url, data, queryParams, tokenOverride, true);
                }
                this.encerrarSessaoExpirada();
            }
            // Sem conexão, timeout ou erro de servidor nessas rotas: o chamador mostra a própria mensagem, sem redirecionar nem snackbar
            const estourouOTempo = error?.name === 'TimeoutError';
            if ((estourouOTempo || this.isServerUnavailable(error.status)) && ROTAS_COM_ERRO_TRATADO_PELO_CHAMADOR.includes(url)) {
                return {
                    success: false,
                    message: '',
                    messageCode: estourouOTempo ? 0 : error.status,
                    data: []
                };
            }
            if (this.isServerUnavailable(error.status) && error.status !== 500) {
                this.redirectToServerError();
                return {
                    success: false,
                    message: '',
                    messageCode: error.status,
                    data: []
                };
            } else if(error.status == 500) {
                this.snackBar.open(this.translateService.instant("api.internalServerError"), '', {
                    duration: 10000,
                    verticalPosition: 'top',
                    panelClass: ['red-snackbar']
                });
            }
            if (error.error instanceof ErrorEvent) {
                return {
                    success: false,
                    message: 'API request failed',
                    messageCode: -1,
                    data: []
                };
            } else {
                // Recusas de autorização chegam só com o código; o texto vem do pt.json
                const mensagem = mensagemDoCodigoDeAutorizacao(this.translateService, error.error?.messageCode);
                return mensagem ? { ...error.error, message: mensagem } : error.error;
            }
        }
    }
}
